import { BlogArticle } from '../../types/blog';
import { articlesBatch1 } from './articlesBatch1';
import { articlesBatch2 } from './articlesBatch2';
import { articlesBatch3 } from './articlesBatch3';
import { articlesBatch4 } from './articlesBatch4';
import { articlesBatch5 } from './articlesBatch5';
import { articlesBatch6 } from './articlesBatch6';

export const ALL_BLOG_ARTICLES: BlogArticle[] = [
  ...articlesBatch1,
  ...articlesBatch2,
  ...articlesBatch3,
  ...articlesBatch4,
  ...articlesBatch5,
  ...articlesBatch6,
];

export function getAllArticles(): BlogArticle[] {
  return ALL_BLOG_ARTICLES;
}

export function getArticleBySlug(slug: string): BlogArticle | undefined {
  const normalized = slug.replace(/^\/+|\/+$/g, '').replace(/^blog\//, '');
  return ALL_BLOG_ARTICLES.find(
    (a) => a.slug === normalized || a.slug === slug
  );
}

export function getArticlesByCategory(categorySlug: string): BlogArticle[] {
  if (!categorySlug || categorySlug === 'all') return ALL_BLOG_ARTICLES;
  return ALL_BLOG_ARTICLES.filter((a) => a.categorySlug === categorySlug);
}

export function getRelatedArticles(
  currentSlug: string,
  limit: number = 3
): BlogArticle[] {
  const current = getArticleBySlug(currentSlug);
  if (!current) return ALL_BLOG_ARTICLES.slice(0, limit);

  // First try direct declared related slugs
  const directRelated = current.relatedSlugs
    .map((s) => getArticleBySlug(s))
    .filter((a): a is BlogArticle => a !== undefined && a.slug !== current.slug);

  if (directRelated.length >= limit) {
    return directRelated.slice(0, limit);
  }

  // Fallback to same category
  const sameCategory = ALL_BLOG_ARTICLES.filter(
    (a) =>
      a.categorySlug === current.categorySlug &&
      a.slug !== current.slug &&
      !directRelated.some((d) => d.slug === a.slug)
  );

  return [...directRelated, ...sameCategory].slice(0, limit);
}

export interface BlogCategorySummary {
  title: string;
  slug: string;
  count: number;
}

export function getAllCategories(): BlogCategorySummary[] {
  const map = new Map<string, { title: string; count: number }>();

  for (const article of ALL_BLOG_ARTICLES) {
    const existing = map.get(article.categorySlug);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(article.categorySlug, {
        title: article.category,
        count: 1,
      });
    }
  }

  const result: BlogCategorySummary[] = [
    { title: 'All Articles', slug: 'all', count: ALL_BLOG_ARTICLES.length },
  ];

  map.forEach((val, key) => {
    result.push({
      title: val.title,
      slug: key,
      count: val.count,
    });
  });

  return result;
}

export function searchArticles(
  query: string,
  categorySlug: string = 'all'
): BlogArticle[] {
  let list = ALL_BLOG_ARTICLES;
  if (categorySlug && categorySlug !== 'all') {
    list = list.filter((a) => a.categorySlug === categorySlug);
  }

  const q = query.trim().toLowerCase();
  if (!q) return list;

  return list.filter((article) => {
    return (
      article.title.toLowerCase().includes(q) ||
      article.excerpt.toLowerCase().includes(q) ||
      article.primaryKeyword.toLowerCase().includes(q) ||
      article.secondaryKeywords.some((k) => k.toLowerCase().includes(q)) ||
      article.category.toLowerCase().includes(q)
    );
  });
}
